import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  InjectionToken,
  ViewContainerRef,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { FormArray, FormControl } from '@angular/forms';

import { select, Store } from '@ngrx/store';
import type { Subscription } from 'rxjs';
import { merge, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { AccountSelectors, CurrentUserSelectors } from '@core/account-data';
import type { CurrentUser } from '@core/account-types';
import { BillingPlanTier } from '@core/billing-types';
import { filterBoolean, trackByProp } from '@core/common-legacy';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import { FeatureGuardDialogComponent, GuardedFeature } from '@core/feature-guard-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { LocationSelectors } from '@core/locations-data';
import type { Location } from '@core/locations-types';
import type { IconToken } from '@core/user-interface-legacy';
import {
  BoxComponent,
  ButtonComponent,
  DialogService,
  DialogWidth,
  EmptyStateComponent,
  HeadingComponent,
  IconComponent,
  ProtectedControlDirective,
} from '@core/user-interface-legacy';

import { LocationItemComponent } from './location-item/location-item.component';
import { AssignLocationsFacade } from '../../services/assign-locations.facade';
import { LocationsActionsService } from '../../services/locations-actions.service';
import { LocationsHelperService } from '../../services/locations-helper.service';
import { ReorderLocationsFacade } from '../../services/reorder-locations.facade';
import { UnassignLocationFacade } from '../../services/unassign-location.facade';

export interface LocationsSectionTranslations {
  heading: string;
  subheading: string;
  assignLocation: string;
  archiveBadgeTooltip: string;
  emptyState: {
    title: string;
    description: string;
  };
}

export enum LocationsSectionViewContext {
  Requisition = 'requisition',
  Job = 'job',
}

export const LocationSectionTranslationsToken = new InjectionToken<LocationsSectionTranslations>(
  'LocationsSectionTranslations',
);

@Component({
  selector: 'rt-locations-section',
  imports: [
    BoxComponent,
    ButtonComponent,
    HeadingComponent,
    IconComponent,
    TranslatePipe,
    EmptyStateComponent,
    CdkDropList,
    CdkDrag,
    LocationItemComponent,
    ProtectedControlDirective,
  ],
  templateUrl: './locations-section.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./locations-section.component.less'],
})
export class LocationsSectionComponent {
  private readonly assignLocationsFacade = inject<AssignLocationsFacade>(AssignLocationsFacade);
  private readonly unassignLocationFacade = inject<UnassignLocationFacade>(UnassignLocationFacade);
  private readonly reorderLocationsFacade = inject<ReorderLocationsFacade>(ReorderLocationsFacade);
  private readonly locationsActionsService = inject(LocationsActionsService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly dialog = inject(DialogService);
  private readonly locationsHelper = inject(LocationsHelperService);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly translations = inject<LocationsSectionTranslations>(
    LocationSectionTranslationsToken,
  );

  public readonly LocationsSectionViewContext = LocationsSectionViewContext;

  public readonly locationsControl = input.required<FormArray<FormControl>>();
  public readonly activeLocationMissingError = input<boolean>(undefined!);
  public readonly activeValidLocationMissingError = input<boolean>(undefined!);
  public readonly exceededLocationsLimitError = input<boolean>(undefined!);
  public readonly readOnly = input<boolean>(undefined!);
  public readonly currentLangCode = input<string>(undefined!);
  public readonly jobPrimaryLangCode = input<string>();
  public readonly jobLanguages = input<string[]>(undefined!);
  public readonly unassingNeedsConfirm = input<boolean>();
  public readonly singleLocationMode = input<boolean>(undefined!);
  public readonly hasEmptyState = input(true);
  public readonly viewContext = input<LocationsSectionViewContext>(undefined!);
  public readonly hideLocationRequiredBadge = input(false);

  public locations: Location[] = [];
  public trackById = trackByProp<Location>({ prop: 'id' });
  public hasManageLocationsAbility: boolean;
  public currentUser: CurrentUser;
  public locationsLimit: number;

  // TEMP after the feature is released, instead of the DevFlag, use rtFeaturePlanIconColor for upgrade icon
  private pp24Enabled = injectDevFlag(DevFlag.PP24Enabled);
  private isOnLegacyPlan = this.store.selectSignal(AccountSelectors.isOnLegacyPlan);
  private locationsChangesSubscription: Subscription | null = null;

  public get hasUpgradeIcon() {
    return this.locationsLimit === 1 ? !!this.locations.length : false;
  }

  public get colorToken() {
    return <IconToken>(
      (this.pp24Enabled && !this.isOnLegacyPlan()
        ? 'colorIconGrowDefault'
        : 'colorIconOptimizeDefault')
    );
  }

  public get showRequired(): boolean {
    return !this.locationsControl().value.length && !this.hideLocationRequiredBadge();
  }

  public get showRequiredError(): boolean {
    const locationsControl = this.locationsControl();
    return locationsControl.errors?.required && locationsControl.touched;
  }

  public get showActiveLocationMissingError(): boolean {
    return this.activeLocationMissingError() && this.locationsControl().touched;
  }

  public get showActiveValidLocationMissingError(): boolean {
    return this.activeValidLocationMissingError() && this.locationsControl().touched;
  }

  public get showExceededLocationLimitError(): boolean {
    return this.exceededLocationsLimitError() && this.locationsControl().touched;
  }

  public get showInvalid() {
    return (
      this.showRequiredError ||
      this.showActiveLocationMissingError ||
      this.showActiveValidLocationMissingError ||
      this.showExceededLocationLimitError
    );
  }

  public ngOnInit() {
    this.setData();
    this.subscribeToLocationsChanges();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.locationsControl?.currentValue) {
      this.subscribeToLocationsChanges();
    }
  }

  public assignLocations() {
    this.assignLocationsFacade.execute(this.vcRef, this.jobPrimaryLangCode(), this.jobLanguages());
    this.cdRef.detectChanges();
  }

  public unassignLocations(id: number) {
    this.unassignLocationFacade.execute(id, this.unassingNeedsConfirm());
    this.cdRef.detectChanges();
  }

  public reorderLocations(event: CdkDragDrop<any>) {
    this.reorderLocationsFacade.execute(event);
    this.cdRef.detectChanges();
  }

  public editLocation(id: number) {
    this.locationsActionsService.fetchOne$(id);
    this.locationsActionsService
      .editLocation$(this.vcRef, {
        id,
        langCode: this.currentLangCode(),
        jobPrimaryLanguageCode: this.jobPrimaryLangCode(),
        jobLanguages: this.jobLanguages(),
      })
      .subscribe();
  }

  public openGuardModal() {
    this.dialog.openCustom(
      FeatureGuardDialogComponent,
      {
        width: DialogWidth.Small,
      },
      {
        feature: GuardedFeature.Locations,
        availableFromPlanOverwrite:
          this.pp24Enabled && !this.isOnLegacyPlan() ? BillingPlanTier.Grow : BillingPlanTier.Scale,
      },
    );
  }

  private sortLocation(locations: Location[]) {
    const locationIds: number[] = this.locationsControl().value || [];
    return locations.sort(
      (locationA, locationB) =>
        locationIds.indexOf(locationA.id) - locationIds.indexOf(locationB.id),
    );
  }

  private subscribeToLocationsChanges() {
    if (this.locationsChangesSubscription) {
      this.locationsChangesSubscription.unsubscribe();
      this.locationsChangesSubscription = null;
    }

    this.locationsChangesSubscription = merge(
      this.locationsControl().valueChanges,
      of(this.locationsControl().value),
    )
      .pipe(
        switchMap(locationIds => this.store.select(LocationSelectors.byIds(locationIds))),
        filterBoolean,
        map(locations => this.sortLocation(locations)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(locations => {
        this.locations = locations || [];
        this.cdRef.detectChanges();
      });
    this.store
      .pipe(
        select(AbilitiesSelectors.hasOne(Ability.ManageLocations)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        hasManageLocationsAbility => (this.hasManageLocationsAbility = hasManageLocationsAbility),
      );
  }

  private setData() {
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean, takeUntilDestroyed(this.destroyRef))
      .subscribe(currentUser => {
        this.currentUser = currentUser;
      });
    this.locationsHelper.getLocationsLimit().subscribe(locationsLimit => {
      this.locationsLimit = locationsLimit;
      this.cdRef.detectChanges();
    });
  }
}
